import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import SettingIcon from "src/assets/svg-icons/side-bar/settings.svg";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import InfoItem from "./info-item";
import PaymentsTable from "./table";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetOCTransactionById } from "src/api/operation-cost/transactions";
import FileItem from "src/shared/components/file-item";
import { downloadFileFromUrl, formatBytes, formatNumber } from "src/utils";
import { useTranslation } from "react-i18next";
import EventPaymentsTable from "./events-table";
import OwnersPaymentsTable from "./owners-payments-table";
import VendorPaymentsTable from "./vendor-payments";

const TransactionDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { pathname } = useLocation();
  const { t } = useTranslation("operationCost");
  const { t: generalT } = useTranslation("general");

  const isAsset = pathname.includes("/assets/cost-list");

  const { data, isLoading, refetch } = useGetOCTransactionById(id || "");

  const navigateToOperationCostList = () => {
    if (isAsset) {
      navigate("/assets/cost-list");
    } else {
      navigate("/operation/cost-list");
    }
  };

  return (
    <Stack gap={3} p={{ xs: 2, sm: 4 }}>
      <Stack
        direction={"row"}
        spacing={"12px"}
        alignItems={"center"}
        color={"#101828"}
      >
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          width={"60px"}
          height={"60px"}
          bgcolor={"#D0D5DD"}
          borderRadius={"16px"}
        >
          <img src={SettingIcon} width={"32px"} alt="" />
        </Box>
        <Typography fontSize={"32px"} lineHeight={"38.4px"}>
          {t("costDetails")}
        </Typography>
      </Stack>
      {/* divider */}
      <Box bgcolor={"#F2F4F7"} width={"100%"} height={"1px"} />

      <Stack spacing={2} direction={"row"} alignItems={"center"}>
        <Typography
          color={"#475467"}
          lineHeight={"19.2px"}
          onClick={navigateToOperationCostList}
          sx={{ cursor: "pointer" }}
        >
          {isAsset ? generalT("fixedAssets") : t("operationExpenses")}
        </Typography>
        <img src={RightArrow} alt="" />
        <Typography
          color={"#475467"}
          lineHeight={"19.2px"}
          sx={{ cursor: "pointer" }}
          onClick={navigateToOperationCostList}
        >
          {t(isAsset ? "fixedAssetsBills" : "expensesList")}
        </Typography>
        <img src={RightArrow} alt="" />
        <Box borderRadius={"4px"} bgcolor={"#EAECF0"} p={"4px 8px"}>
          <Typography color={"#344054"} fontWeight={600} lineHeight={"19.2px"}>
            {data?.data?.account?.name}
          </Typography>
        </Box>
      </Stack>
      {isLoading && !data ? (
        <Box display={"flex"} justifyContent={"center"} pt={20}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Stack bgcolor={"#F9FAFB"} spacing={1.5} p={2}>
            <Typography
              fontSize={"14px"}
              color={"#344054"}
              fontWeight={700}
              lineHeight={"18.8px"}
            >
              {generalT("branch")}:
              <Typography
                component={"span"}
                fontWeight={400}
                fontSize={"14px"}
                lineHeight={"18.8px"}
              >
                {" "}
                {data?.data.branch.name}
              </Typography>
            </Typography>
            <InfoItem
              title={generalT("accountType")}
              text={
                data?.data.account?.type === "variable"
                  ? t("variable")
                  : t("recurringExpenses")
              }
              label=""
              value=""
            />
            <InfoItem
              title={generalT("subtotal")}
              text={`${formatNumber(data?.data.amount)}`}
              label={generalT("status")}
              value={
                !data?.data?.due_date && data?.data?.account.type === "asset"
                  ? t("deprecation")
                  : data?.data.status === "Paid"
                    ? t("paid")
                    : data?.data.status === "Partly Paid"
                      ? t("partiallyPaid")
                      : data?.data.status === "UnPaid"
                        ? t("unpaid")
                        : data?.data.status === "Reminder"
                          ? t("reminder")
                          : t("overpaid")
              }
            />
            <InfoItem
              title={generalT("tax")}
              text={`${formatNumber(data?.data.tax)}`}
              label={generalT("totalWithTax")}
              value={data?.data.total}
            />
            <InfoItem
              title={t("invoiceNumber")}
              text={`${data?.data.invoice_number || ""}`}
              label={generalT("createdBy")}
              value={`${data?.data.created_by?.name}`}
            />
            <InfoItem
              title={t("notes")}
              text={`${data?.data.note || ""}`}
              label={t("dueDate")}
              value={`${data?.data.due_date}`}
            />
            <InfoItem
              title={t("repeat")}
              text={data?.data.repeat_name}
              label={t("billBalance")}
              value={`${
                data?.data.total_remain === 0
                  ? data?.data.total_remain
                  : formatNumber(data?.data.total_remain)
              }`}
            />
            <InfoItem title={t("attachedBill")} label="" value="" />
            <Stack gap={3} direction={"row"} maxWidth={"50%"} flexWrap={"wrap"}>
              {data?.data.files.map((attachment: any) => {
                return (
                  <FileItem
                    isDeletable={false}
                    isDownloadable
                    key={attachment.id}
                    name={`${attachment.file.name} .${attachment.file.extension}`}
                    info={formatBytes(attachment.file.size)}
                    onDownload={() => {
                      downloadFileFromUrl(
                        attachment.file.url,
                        attachment.file.name,
                      );
                    }}
                  />
                );
              })}
            </Stack>
          </Stack>
          <PaymentsTable
            list={data.data.payments}
            isLoading={isLoading}
            refetch={refetch}
          />
          <EventPaymentsTable
            list={data.data.purchase_events || []}
            isLoading={isLoading}
            refetch={refetch}
          />
          <OwnersPaymentsTable
            list={data.data.invoice_capitals || []}
            isLoading={isLoading}
            refetch={refetch}
          />
          <VendorPaymentsTable
            list={data.data.invoice_balance_payments || []}
            isLoading={isLoading}
            refetch={refetch}
          />
        </>
      )}
    </Stack>
  );
};

export default TransactionDetails;
